import Image from "next/image";
import { useLoginModal } from "@/utils/useLoginModal";
import { NormalizedRollingTableRow, TableRollingReturns } from "@/type/rollingPageTypes";
import { buildRollingInsightsTableData } from "@/utils/rollingReturnsCalculation";

export default function RollingInsightsTable({ tableData }: { tableData: TableRollingReturns[] }) {
  const { isLoggedIn, open, setModalKey } = useLoginModal();

  const tableRows = buildRollingInsightsTableData(tableData, isLoggedIn);

  return (
    <div className="relative rounded-none lg:rounded-6xl border border-[#d2d5da] bg-white overflow-hidden">
      <div className={`w-full relative scrollbar-xs ${isLoggedIn ? "overflow-x-auto" : "overflow-hidden"}`}>
        <table className="min-w-max w-full border-collapse rounded-3xl [&_th:nth-child(2)]:border-r-[#555555] [&_td:nth-child(2)]:border-r-[#555555] [&_th:nth-child(6)]:border-r-[#555555] [&_td:nth-child(6)]:border-r-[#555555]">
          <thead>
            <tr className="text-primaryGreen">
              <th colSpan={2} />
              <th colSpan={4} className="border-x border-[#555555] font-bold py-4">
                RETURN STATS. (%)
              </th>
              <th colSpan={7} className="py-4">
                RETURN DISTRIBUTION (% OF TIMES)
              </th>
            </tr>

            <tr className="border-y border-[#d2d5da]">
              {tableRows.columns.map((item) => (
                <th key={item} className="py-3 px-2 xs:first-of-type:pl-5 border-r border-[#d2d5da] last-of-type:border-r-0">
                  <div className="text-sm font-medium text-primaryBlack first-of-type:text-left">{item}</div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {tableRows.rows.map((row, rowIndex) => (
              <tr key={rowIndex} className="border-b border-[#d2d5da] last-of-type:border-b-0">
                {tableRows.columns.map((column, colIndex) => {
                  const isConsistency = column === "Consistency";

                  if (isConsistency) {
                    const consistency = row.Consistency;

                    return (
                      <td key={colIndex} className="py-3 px-2 xs:first-of-type:pl-5 text-center first:text-left text-2xs text-[#37414F] border-r border-[#d2d5da] last-of-type:border-r-0">
                        <div
                          className="truncate max-w-40 xs:max-w-52"
                          {...(consistency
                            ? {
                                "data-consistency-percentage": consistency.consistencyPercentage,
                                "data-total-days": consistency.totalDays,
                                "data-outperform-days": consistency.outperformDays,
                                "data-underperform-days": consistency.underperformDays,
                                "data-equal-days": consistency.equalDays,
                              }
                            : {})}
                        >
                          {consistency?.consistencyPercentage ?? "-"}
                        </div>
                      </td>
                    );
                  }

                  // All other columns are string | number
                  const safeValue = row[column as Exclude<keyof NormalizedRollingTableRow, "Consistency">];

                  return (
                    <td key={colIndex} className="py-3 px-2 xs:first-of-type:pl-5 text-center first:text-left text-2xs text-[#37414F] border-r border-[#d2d5da] last-of-type:border-r-0">
                      <div className="truncate max-w-40 xs:max-w-52">{safeValue ?? "-"}</div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>

        {!isLoggedIn && (
          <div className="w-full absolute inset-0 backdrop-blur-xs z-2 bg-white/75 overflow-hidden flex items-center justify-center px-5">
            <Image src="/next_assets/images/rolling-returns-pages/newlock.png" alt="lock" width={100} height={200} className="block object-contain w-16 xs:w-20 h-auto md:w-32 md:absolute md:-right-7 md:-bottom-14" />

            <div>
              <div className="text-center font-semibold text-lg sm:text-xl sm:leading-7 leading-1.3 mb-1.5">Unlock Detailed Rolling Return Analysis</div>
              <div className="px-8 sm:px-0 text-black/50 text-2xs md:text-2sm md:leading-5.5 mx-auto leading-1.3 text-center mb-3 sm:mb-4">Login now to see Premium Comparison details now.</div>

              <button
                onClick={() => {
                  open();
                  setModalKey("rolling-return-page-table");
                }}
                className="mx-auto block w-full max-w-52 px-7 py-2.5 sm:py-3 rounded-xl text-sm sm:text-base border border-white font-bold text-white bg-[linear-gradient(125.62deg,#E0DDF5_1.99%,#5D00DF_73.84%)] shine-button-hover"
              >
                Login Now
              </button>
            </div>

            <Image src="/next_assets/images/rolling-returns-pages/new.png" alt="new" width={99} height={157} className="absolute top-0 left-0 hidden md:block" />
          </div>
        )}
      </div>
    </div>
  );
}
